import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HeartPulse, Loader2, Languages, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t, toggleLang, lang } = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fill = (em) => { setEmail(em); setPassword(em.includes("admin") ? "Admin123!" : "Employee123!"); }; // eslint-disable-line no-unused-vars


  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-sidebar sidebar-noise text-sidebar-foreground overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/20 blur-3xl" />
        <Link to="/" className="flex items-center gap-3 relative z-10 group" data-testid="brand-home-link">
          <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center">
            <HeartPulse className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-display text-xl font-extrabold leading-none">{t("appName")}</p>
            <p className="text-sidebar-muted text-xs mt-1">{t("tagline")}</p>
          </div>
        </Link>
        <div className="relative z-10 space-y-6">
          <h1 className="font-display text-4xl font-extrabold tracking-tight leading-tight">
            {lang === "fr" ? "Gérez votre résidence, en toute simplicité." : "Manage your residence, effortlessly."}
          </h1>
          <p className="text-sidebar-muted text-lg max-w-md">
            {lang === "fr"
              ? "Horaires, tâches, communication et dossiers d'employés — tout au même endroit."
              : "Scheduling, tasks, communication and employee records — all in one place."}
          </p>
          <div className="flex gap-6 pt-4">
            {[t("feat1"), t("feat2"), t("feat3")].map((f) => (
              <div key={f} className="text-sm text-sidebar-muted border-l-2 border-primary pl-3">{f}</div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-sidebar-muted text-sm">© 2026 Residence Connect · Québec</div>
      </div>

      {/* Right form */}
      <div className="flex flex-col items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-fade-up">
          <div className="flex justify-between items-center mb-8">
            <Link to="/" data-testid="back-to-home"
              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" /> {lang === "fr" ? "Accueil" : "Home"}
            </Link>
            <button onClick={toggleLang} data-testid="login-lang-toggle"
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <Languages className="w-4 h-4" /> {lang === "fr" ? "English" : "Français"}
            </button>
          </div>

          <h2 className="font-display text-3xl font-bold tracking-tight">{t("loginTitle")}</h2>
          <p className="text-muted-foreground mt-2 mb-8">{t("loginSubtitle")}</p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@residence.ca" required data-testid="login-email-input" autoComplete="username" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("password")}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required data-testid="login-password-input" autoComplete="current-password" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full h-11" disabled={loading} data-testid="login-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("signIn")}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {t("noAccountYet")} <Link to="/register" className="text-primary font-medium hover:underline" data-testid="go-to-register">{t("signUp")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
